import { base64ArrayBuffer } from '@common-util/buffer-util';
import { getRotatedDimensions, scaleImagePreserveAspect } from '@common-util/texture-util';
import { SvgTextureLayer } from '@models/classes/materials/svg-texture-layer';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { TextureParameters } from '../texture-stack';
import { IDrawOperations, TextureLayerRenderer } from './texture';

export class SvgTextureLayerRenderer extends TextureLayerRenderer<SvgTextureLayer> {
    modelToken = Token.FileTextureLayer;
    imageElement = document.createElement('img');
    svgHelpers = new Map<
        string,
        {
            imgElement: HTMLImageElement;
            renderedWidth: number;
            renderedHeight: number;
        }
    >();

    paintTexture(
        texture: SvgTextureLayer,
        context: CanvasRenderingContext2D,
        parameters: TextureParameters,
        operations: IDrawOperations
    ) {
        const helper = this.svgHelpers.get(texture._dynamicId);

        if (
            texture._raster &&
            helper &&
            context.canvas.width === helper.renderedWidth &&
            context.canvas.height === helper.renderedHeight
        ) {
            return this.drawSourceToCanvas(texture, texture._raster, context, parameters, operations);
        } else {
            let fontLoadPromise: Promise<Record<string, ArrayBuffer> | void>;

            if (texture.preloadFontStylesheet) {
                const fontFacesFound = [...texture.svgXml.matchAll(/font-family\s*[=:]\s*["'](.+?)["']/g)];
                const fontsToLoad: string[] = [];
                for (const match of fontFacesFound) {
                    fontsToLoad.push(match[1]);
                }

                if (fontsToLoad.length > 0) {
                    const manager = this.viewer.sceneNode._manager!;
                    const fontService = manager.getService(Token.FontService);

                    fontLoadPromise = fontService.loadFontData(texture.preloadFontStylesheet, fontsToLoad);
                } else {
                    fontLoadPromise = Promise.resolve();
                }
            } else {
                fontLoadPromise = Promise.resolve();
            }

            fontLoadPromise
                .then(result =>
                    this.generateSvg(texture, context.canvas.width, context.canvas.height, result || undefined)
                )
                .then(bitmap => {
                    if (bitmap) {
                        texture._raster = bitmap;
                    }
                })
                .catch(() => {
                    // Don't need to do anything
                });
        }
    }

    textureHasContent(node: SvgTextureLayer) {
        return !!node.svgXml;
    }

    getNativeSize(texture: SvgTextureLayer) {
        const width = texture.width,
            height = texture.height;

        if (texture.wAngle !== 0) {
            return getRotatedDimensions(width, height, texture.wAngle);
        } else {
            return { width, height };
        }
    }

    update3dInternal(node: ITrackedClass<SvgTextureLayer>) {
        if (
            node.changes.has('svgXml') ||
            node.changes.has('width') ||
            node.changes.has('height') ||
            node.changes.has('preloadFontStylesheet')
        ) {
            node._raster = undefined;
        }
        super.update3dInternal(node);
    }

    generateSvg(texture: SvgTextureLayer, width: number, height: number, embedFonts?: Record<string, ArrayBuffer>) {
        if (texture.svgXml !== '' && width > 0 && height > 0) {
            let helper = this.svgHelpers.get(texture._dynamicId);

            if (!helper) {
                helper = {
                    imgElement: document.createElement('img'),
                    renderedHeight: height,
                    renderedWidth: width,
                };
                this.svgHelpers.set(texture._dynamicId, helper);
            } else {
                helper.renderedHeight = height;
                helper.renderedWidth = width;
            }

            try {
                const imageElement = this.svgHelpers.get(texture._dynamicId)!.imgElement;

                let svgString = texture.svgXml;

                // Adding svg required tags
                if (!svgString.match(/<!DOCTYPE/)) {
                    svgString =
                        `<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">` +
                        svgString;
                }
                if (!svgString.match(/<\?xml/)) {
                    svgString = `<?xml version="1.0" encoding="UTF-8"?>` + svgString;
                }
                if (!svgString.match(/<svg [^>]*xmlns=[^>]*>/)) {
                    svgString = svgString.replace(/<svg /, '<svg xmlns="http://www.w3.org/2000/svg" ');
                }

                const match = svgString.match(/<svg[\s\S]*?>/m);
                if (!match) {
                    console.warn('Invalid SVG');
                    return Promise.reject();
                } else {
                    if (embedFonts) {
                        let fontStyles = '';
                        for (const fontFamily in embedFonts) {
                            const arrayBuffer = embedFonts[fontFamily];
                            fontStyles += `       
@font-face {
    font-family: "${fontFamily}";
    src: url("data:application/font-woff;charset=utf-8;base64,${base64ArrayBuffer(arrayBuffer)}");
}
                                `;
                        }
                        const offset = (match.index || 0) + match[0].length;

                        svgString =
                            svgString.substring(0, offset) +
                            `<style type="text/css">${fontStyles}</style>` +
                            svgString.substring(offset);
                    }

                    const blobUrl = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' }));
                    imageElement.src = blobUrl;

                    const generateImagePromise = imageElement.decode().then(
                        () => {
                            // Don't use the img element from the closure so it can get garbage collected if this function isn't called!
                            // There's no way to explicitly dispose of a DOM element
                            const loadedImageElement = this.svgHelpers.get(texture._dynamicId)!.imgElement;

                            // Figure out what resolution to actually draw the SVG at to fit within the envelope
                            const scaled = scaleImagePreserveAspect(
                                width,
                                height,
                                loadedImageElement.naturalWidth,
                                loadedImageElement.naturalHeight
                            );

                            return createImageBitmap(loadedImageElement, {
                                resizeWidth: scaled.drawWidth,
                                resizeHeight: scaled.drawHeight,
                            });
                        },
                        err => {
                            console.warn('Could not decode SVG XML: ', svgString);
                        }
                    );

                    this.viewer.deferSceneReady(generateImagePromise);
                    return generateImagePromise;
                }
            } catch (e) {
                console.warn('Could not generate SVG Texture: ', e);
                Promise.reject(e);
            }
        }

        return Promise.reject();
    }

    delete3d(node: ITrackedClass<SvgTextureLayer>) {
        const helper = this.svgHelpers.get(node._dynamicId);
        if (helper) {
            this.svgHelpers.delete(node._dynamicId);
        }
        super.delete3d(node);
    }
}
